/**
 * VenueOverviewMap — Sprint L two-tier buyer map.
 *
 * Renders the venue as a top-down SVG plan. Each section is drawn from its
 * immutable snapshot geometry (x_percent, y_percent, width_percent,
 * height_percent, rotation_deg). Section fill colour comes from the assigned
 * pricing zone; the overlay dims / stripes to reflect live availability
 * (available | selling_fast | almost_sold | sold_out).
 *
 * Decorative sections (STAGE, MAIN ENTRANCE, BROADCASTING, etc.) render as
 * labelled tiles but are never interactive and never counted against
 * inventory — enforced at the seat-generation layer.
 *
 * Contract:
 *   props.sections           — snapshot sections with geometry (immutable)
 *   props.zones              — snapshot pricing zones (immutable)
 *   props.availability       — array from /section-availability
 *   props.onSelectSection    — (sectionName) => void
 *   props.viewportWidth/Height — from layout doc, defaults 100x100
 *   props.selectedSectionCounts — { [sectionName]: number } picked in cart
 */
import React, { useMemo } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, Maximize2, Ticket } from "lucide-react";

// Fallback fill when a section is decorative or has no zone
const DECOR_FILL = "rgba(9,9,11,0.75)";
const DECOR_TEXT = "#f4f4f5";
const NO_ZONE_FILL = "rgba(9,9,11,0.06)";
const NO_ZONE_STROKE = "rgba(9,9,11,0.2)";

// Convert a hex zone colour to a light tint for the fill, but honour the
// availability state (fade to grey for sold_out, cross-hatch for almost_sold)
function statusStyles(baseHex, status) {
  const hex = baseHex || "#71717a";
  if (status === "sold_out") {
    return { fill: "rgba(9,9,11,0.12)", stroke: "rgba(9,9,11,0.28)", labelColor: "rgba(9,9,11,0.55)" };
  }
  // Slightly translucent so blocks feel tinted, not solid poster paint
  return {
    fill: hex,
    stroke: hex,
    labelColor: pickReadableText(hex),
    dim: status === "almost_sold" ? 0.75 : 1,
  };
}

// Compute readable text colour for a given hex background (rough luminance)
function pickReadableText(hex) {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return l > 0.6 ? "#09090b" : "#ffffff";
  } catch {
    return "#09090b";
  }
}

// Human badge text per availability status
const STATUS_LABEL = {
  available: null,
  selling_fast: "Selling fast",
  almost_sold: "Almost sold",
  sold_out: "Sold out",
};

export default function VenueOverviewMap({
  sections = [],
  zones = [],
  availability = [],
  onSelectSection,
  viewportWidth = 100,
  viewportHeight = 100,
  selectedSectionCounts = {},
  activeSection = null,
}) {
  const zonesById = useMemo(
    () => Object.fromEntries((zones || []).map((z) => [z.id, z])),
    [zones],
  );
  const availByName = useMemo(
    () => Object.fromEntries((availability || []).map((a) => [a.section, a])),
    [availability],
  );

  // Filter sections that have valid geometry — the parent component picks the
  // fallback map when none of them do.
  const placedSections = useMemo(
    () => sections.filter(
      (s) => s.x_percent != null && s.y_percent != null
        && s.width_percent != null && s.height_percent != null,
    ),
    [sections],
  );

  const vw = Number(viewportWidth) || 100;
  const vh = Number(viewportHeight) || 100;

  if (placedSections.length === 0) return null;

  return (
    <div
      className="mt-10 border-t border-[var(--gt-line)] pt-8"
      data-testid="venue-overview-map"
    >
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div className="gt-label flex items-center gap-2">
          <Ticket size={12} /> Pick a section to see seats
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
          Tap a coloured block · pinch to zoom
        </div>
      </div>

      <TransformWrapper
        initialScale={1}
        minScale={0.6}
        maxScale={5}
        wheel={{ step: 0.15 }}
        doubleClick={{ mode: "reset" }}
        limitToBounds
        pinch={{ step: 5 }}
        panning={{ velocityDisabled: true }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <div className="relative">
            <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
              <button
                type="button"
                onClick={() => zoomIn()}
                aria-label="Zoom in"
                data-testid="overview-zoom-in"
                className="w-8 h-8 rounded-sm bg-white border border-[var(--gt-line)] flex items-center justify-center shadow-sm hover:border-[var(--gt-ink)]"
              >
                <ZoomIn size={14} />
              </button>
              <button
                type="button"
                onClick={() => zoomOut()}
                aria-label="Zoom out"
                data-testid="overview-zoom-out"
                className="w-8 h-8 rounded-sm bg-white border border-[var(--gt-line)] flex items-center justify-center shadow-sm hover:border-[var(--gt-ink)]"
              >
                <ZoomOut size={14} />
              </button>
              <button
                type="button"
                onClick={() => resetTransform()}
                aria-label="Reset view"
                data-testid="overview-reset"
                className="w-8 h-8 rounded-sm bg-white border border-[var(--gt-line)] flex items-center justify-center shadow-sm hover:border-[var(--gt-ink)]"
              >
                <Maximize2 size={14} />
              </button>
            </div>

            <TransformComponent
              wrapperStyle={{ width: "100%" }}
              contentStyle={{ width: "100%" }}
            >
              <svg
                viewBox={`0 0 ${vw} ${vh}`}
                width="100%"
                style={{
                  minHeight: "380px",
                  maxHeight: "70vh",
                  background: "var(--gt-bg-surface, #fafafa)",
                  borderRadius: "4px",
                  touchAction: "none",
                }}
                role="img"
                aria-label="Venue overview — tap a section to browse seats"
                data-testid="overview-svg"
              >
                {placedSections.map((s) => {
                  const avail = availByName[s.name];
                  const status = avail?.status || "available";
                  const zone = s.pricing_zone_id ? zonesById[s.pricing_zone_id] : null;
                  const isDecor = !!s.is_decorative;
                  const style = isDecor
                    ? { fill: DECOR_FILL, stroke: DECOR_FILL, labelColor: DECOR_TEXT }
                    : zone
                    ? statusStyles(zone.hex_colour, status)
                    : statusStyles(null, status);

                  // If section has no zone AND is not decorative, use neutral fill
                  const fill = isDecor
                    ? DECOR_FILL
                    : zone
                    ? style.fill
                    : NO_ZONE_FILL;
                  const stroke = isDecor
                    ? DECOR_FILL
                    : zone
                    ? style.stroke
                    : NO_ZONE_STROKE;
                  const opacity = isDecor ? 0.9 : (style.dim ?? 1);
                  const cx = s.x_percent + s.width_percent / 2;
                  const cy = s.y_percent + s.height_percent / 2;
                  const rot = s.rotation_deg || 0;
                  const picked = selectedSectionCounts[s.name] || 0;
                  const isSoldOut = !isDecor && status === "sold_out";
                  const isInteractive = !isDecor && !isSoldOut;
                  const isActive = !isDecor && activeSection && activeSection === s.name;
                  const testid = isDecor
                    ? `overview-decor-${s.name}`
                    : `overview-section-${s.name}`;

                  return (
                    <g
                      key={s.id}
                      transform={`rotate(${rot}, ${cx}, ${cy})`}
                      data-testid={testid}
                      data-status={status}
                      style={{ cursor: isInteractive ? "pointer" : "default" }}
                      onClick={() => {
                        if (isInteractive && onSelectSection) onSelectSection(s.name);
                      }}
                      onKeyDown={(e) => {
                        if (!isInteractive) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectSection?.(s.name);
                        }
                      }}
                      tabIndex={isInteractive ? 0 : -1}
                      role={isInteractive ? "button" : "presentation"}
                      aria-label={
                        isDecor
                          ? `${s.name} (decorative)`
                          : `${s.name} — ${STATUS_LABEL[status] || "Available"}${zone ? `, from A$${((zone.base_price_cents || 0) / 100).toFixed(2)}` : ""}`
                      }
                    >
                      <rect
                        x={s.x_percent}
                        y={s.y_percent}
                        width={s.width_percent}
                        height={s.height_percent}
                        rx={s.border_radius_percent ?? 1.5}
                        ry={s.border_radius_percent ?? 1.5}
                        fill={fill}
                        stroke={isActive ? "#09090b" : stroke}
                        strokeWidth={isActive ? 1.2 : 0.3}
                        opacity={opacity}
                      />
                      {/* Active section pulse ring */}
                      {isActive && (
                        <rect
                          x={s.x_percent - 0.6}
                          y={s.y_percent - 0.6}
                          width={s.width_percent + 1.2}
                          height={s.height_percent + 1.2}
                          rx={(s.border_radius_percent ?? 1.5) + 0.6}
                          ry={(s.border_radius_percent ?? 1.5) + 0.6}
                          fill="none"
                          stroke="#09090b"
                          strokeWidth={0.4}
                          strokeDasharray="0.8 0.6"
                          data-testid={`overview-section-active-ring-${s.name}`}
                        />
                      )}
                      {/* Sold-out hatch overlay */}
                      {isSoldOut && (
                        <rect
                          x={s.x_percent}
                          y={s.y_percent}
                          width={s.width_percent}
                          height={s.height_percent}
                          rx={s.border_radius_percent ?? 1.5}
                          ry={s.border_radius_percent ?? 1.5}
                          fill="url(#gt-hatch-soldout)"
                        />
                      )}
                      {/* Section label */}
                      {(s.label_display || "always") !== "hidden" && (
                        <text
                          x={cx}
                          y={cy}
                          fill={isDecor ? DECOR_TEXT : (zone ? style.labelColor : "var(--gt-ink)")}
                          fontSize={Math.min(3.2, s.height_percent * 0.35)}
                          fontFamily="system-ui, sans-serif"
                          fontWeight="700"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{ pointerEvents: "none", letterSpacing: 0.4 }}
                        >
                          {s.name}
                        </text>
                      )}
                      {/* Live availability badge for interactive sections */}
                      {!isDecor && avail && (
                        <text
                          x={cx}
                          y={cy + Math.min(3.2, s.height_percent * 0.35) * 0.9}
                          fill={zone ? style.labelColor : "var(--gt-ink-2)"}
                          fontSize={Math.min(2.0, s.height_percent * 0.22)}
                          fontFamily="system-ui, sans-serif"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{ pointerEvents: "none", letterSpacing: 0.3, opacity: 0.9 }}
                        >
                          {status === "sold_out"
                            ? "SOLD OUT"
                            : status === "almost_sold"
                            ? "Limited availability"
                            : status === "selling_fast"
                            ? "Selling fast"
                            : "Available"}
                        </text>
                      )}
                      {/* Picked-in-cart badge */}
                      {picked > 0 && (
                        <g>
                          <circle
                            cx={s.x_percent + s.width_percent - 1.5}
                            cy={s.y_percent + 1.5}
                            r={1.6}
                            fill="var(--gt-accent, #ef4444)"
                          />
                          <text
                            x={s.x_percent + s.width_percent - 1.5}
                            y={s.y_percent + 1.5}
                            fill="#fff"
                            fontSize={1.6}
                            fontFamily="system-ui, sans-serif"
                            fontWeight="800"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{ pointerEvents: "none" }}
                          >
                            {picked}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}

                {/* SVG defs (hatch pattern for sold-out sections) */}
                <defs>
                  <pattern
                    id="gt-hatch-soldout"
                    patternUnits="userSpaceOnUse"
                    width="2"
                    height="2"
                    patternTransform="rotate(45)"
                  >
                    <rect width="2" height="2" fill="transparent" />
                    <line x1="0" y1="0" x2="0" y2="2" stroke="rgba(9,9,11,0.35)" strokeWidth="0.4" />
                  </pattern>
                </defs>
              </svg>
            </TransformComponent>

            <p className="mt-3 text-[10px] text-center uppercase tracking-widest text-[var(--gt-ink-3)]">
              Pinch or scroll to zoom · drag to pan · tap a section to open it
            </p>
          </div>
        )}
      </TransformWrapper>
    </div>
  );
}
